import React, { useState, useContext, useEffect } from 'react';
import { rest } from '@karpeleslab/klbfw';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Grid, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import Button from '../../core/input/Button';
import { UserContext } from '../../../context/UserContext';

const DeleteAccountDialog = ({ open, setOpen }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const [flowData, setFlowData] = useState(null);
	const [formData, setFormData] = useState({});
	const [, setUser] = useContext(UserContext);

	// Initialize flow when dialog opens
	useEffect(() => {
		if (open && !flowData) {
			handleInitialFlow();
		}
	}, [open]);

	const handleInitialFlow = async () => {
		setIsLoading(true);
		setError('');

		try {
			const res = await rest('User:flow', 'POST', { action: 'delete_account' });

			if (res?.result === 'success' && res.data) {
				setFlowData(res.data);

				// Initialize form data with default values from fields
				const initialFormData = { session: res.data.session };
				res.data.fields?.forEach(field => {
					if (field.default !== undefined && field.default !== null) {
						initialFormData[field.name] = field.default;
					}
				});
				setFormData(initialFormData);

				// Check if already complete (unlikely on initial call)
				if (res.data.complete) {
					setUser(prev => ({ ...prev, showExpiredModal: true }));
				}
			} else {
				setError(res?.error || 'Failed to initiate account deletion. Please try again.');
			}
		} catch (err) {
			setError(err.message || 'Failed to initiate account deletion. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleSubmit = async (autoConfirm = false) => {
		setIsLoading(true);
		setError('');

		try {
			// Prepare submission data
			const submitData = { ...formData };
			if (autoConfirm) {
				submitData.confirm_delete = true;
			}

			// Handle field name mapping for mismatches between req and actual field names
			// If req includes "delete_send_email" but form has "reset_send_email", map it
			if (flowData.req && flowData.fields) {
				flowData.req.forEach(reqFieldName => {
					if (!submitData[reqFieldName]) {
						// Try to find a matching field by pattern
						// For delete_send_email -> reset_send_email mapping
						const matchingField = flowData.fields.find(f =>
							f.name &&
							f.name !== reqFieldName &&
							(f.name.includes('send_email') || reqFieldName.includes('send_email'))
						);

						if (matchingField && formData[matchingField.name] !== undefined) {
							submitData[reqFieldName] = formData[matchingField.name];
						}
					}
				});
			}

			const res = await rest('User:flow', 'POST', submitData);

			if (res?.result === 'success' && res.data) {
				if (res.data.complete) {
					// Account deletion complete - trigger logout
					setUser(prev => ({ ...prev, showExpiredModal: true }));
				} else {
					// Check if we need to auto-confirm
					if (res.data.req?.includes('confirm_delete') && !autoConfirm) {
						// Recursively call with confirm_delete
						setFormData(prev => ({
							...prev,
							session: res.data.session
						}));
						await handleSubmit(true);
						return;
					}

					// Update flow data with new fields/requirements
					setFlowData(res.data);
					setFormData({
						session: res.data.session
					});

					// Initialize any new fields with defaults
					res.data.fields?.forEach(field => {
						if (field.default !== undefined && field.default !== null) {
							setFormData(prev => ({
								...prev,
								[field.name]: field.default
							}));
						}
					});
				}
			} else {
				if (res?.code === 400 && res?.exception === 'Exception\\Field') {
					setError('Invalid input. Please check your entries and try again.');
				} else {
					setError(res?.error || 'Failed to process request. Please try again.');
				}
			}
		} catch (err) {
			setError(err.message || 'Failed to process request. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleCancel = () => {
		setOpen(false);
		setFlowData(null);
		setFormData({});
		setError('');
		setIsLoading(false);
	};

	const handleFieldChange = (fieldName, value) => {
		setFormData(prev => ({
			...prev,
			[fieldName]: value
		}));
	};

	const renderField = (field) => {
		const isRequired = flowData.req?.includes(field.name);

		// Label type - just display text
		if (field.type === 'label') {
			return (
				<DialogContentText key={field.label} style={{ marginBottom: '8px' }}>
					{field.label}
				</DialogContentText>
			);
		}

		// Only render input category fields
		if (field.cat !== 'input') {
			return null;
		}

		// Checkbox type
		if (field.type === 'checkbox') {
			return (
				<FormControlLabel
					key={field.name}
					control={
						<Checkbox
							checked={!!formData[field.name]}
							onChange={(e) => handleFieldChange(field.name, e.target.checked)}
							color="secondary"
						/>
					}
					label={field.label}
					style={{ marginTop: '8px', marginBottom: '8px' }}
				/>
			);
		}

		// Text, email, password types
		if (field.type === 'text' || field.type === 'email' || field.type === 'password') {
			// Check if it's an OTP field
			const isOtp = field.hint === 'otp';

			return (
				<TextField
					key={field.name}
					label={field.label}
					type={isOtp ? 'text' : field.type}
					fullWidth
					variant="outlined"
					margin="normal"
					value={formData[field.name] || ''}
					onChange={(e) => handleFieldChange(field.name, e.target.value)}
					required={isRequired}
					disabled={isLoading}
					inputProps={isOtp ? { maxLength: field.otp?.length || 6 } : {}}
				/>
			);
		}

		return null;
	};

	if (isLoading && !flowData) {
		return (
			<Dialog
				open={open}
				aria-labelledby="delete-loading-title"
			>
				<DialogTitle id="delete-loading-title">
					Loading...
				</DialogTitle>
				<DialogContent>
					<Grid container spacing={2} alignItems="center" justify="center" direction="column">
						<Grid item>
							<CircularProgress />
						</Grid>
						<Grid item>
							<DialogContentText>
								Please wait...
							</DialogContentText>
						</Grid>
					</Grid>
				</DialogContent>
			</Dialog>
		);
	}

	if (!flowData) {
		return null;
	}

	// Check if required fields are filled
	const isFormValid = () => {
		if (!flowData.req) return true;

		return flowData.req.every(reqFieldName => {
			// Check if the field exists directly
			if (formData[reqFieldName]) return true;

			// Try to find a matching field for mismatched names
			const matchingField = flowData.fields?.find(f =>
				f.name &&
				f.name !== reqFieldName &&
				(f.name.includes('send_email') || reqFieldName.includes('send_email'))
			);

			return matchingField && formData[matchingField.name];
		});
	};

	return (
		<Dialog
			open={open}
			onClose={handleCancel}
			aria-labelledby="delete-account-title"
		>
			<DialogTitle id="delete-account-title">
				{flowData.message || 'Delete Account'}
			</DialogTitle>
			<DialogContent>
				{error && (
					<Typography variant="body2" style={{ color: 'red', marginBottom: '16px' }}>
						⚠️ {error}
					</Typography>
				)}
				{flowData.fields?.map(field => renderField(field))}
			</DialogContent>
			<DialogActions>
				<Button
					color="primary"
					onClick={handleCancel}
					disabled={isLoading}
				>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="secondary"
					onClick={handleSubmit}
					disabled={isLoading || !isFormValid()}
				>
					{isLoading ? 'Processing...' : 'Continue'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DeleteAccountDialog;

DeleteAccountDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	setOpen: PropTypes.func.isRequired
};
